import React from 'react'

import { DevTool } from '@hookform/devtools'
import { set } from 'lodash'
import { FormProvider, type Resolver, useForm, useWatch } from 'react-hook-form'

import { FormWarningsContext } from './formWarnings'

// Convert redux-form nested/arrayed errors to the nested shape RHF looks up by field path (lodash `set` makes numeric segments array indices; flat dot-keys wouldn't match).
const toRhfErrors = (errs: any, base = '', out: Record<string, any> = {}): Record<string, any> => {
  Object.entries(errs || {}).forEach(([key, val]) => {
    const path = base ? `${base}.${key}` : key
    if (val && typeof val === 'object') {
      toRhfErrors(val, path, out)
    } else if (val) {
      set(out, path, { type: 'validate', message: String(val) })
    }
  })
  return out
}

// Forward form props as the resolver's 2nd arg so validators keep redux-form's `validate(values, props)` signature.
const withFormResolver =
  (validate: (values: any, props: any) => any): Resolver<any> =>
  async (values, context) => {
    const errors = toRhfErrors(validate(values, context))
    return { values: Object.keys(errors).length ? {} : values, errors }
  }

type WithFormConfig = {
  form?: string
  validate?: (values: any, props?: any) => any
  initialValues?: Record<string, any>
  enableReinitialize?: boolean
  onChange?: (values: any, dispatch: any, props: any) => void
  warn?: (values: any) => any
  // Remaining redux-form config keys (touchOnBlur, keepDirtyOnReinitialize, ...) are accepted and ignored.
  [key: string]: any
}

// RHF replacement for redux-form's `reduxForm({ form, validate })(Component)` HOC: wraps in a FormProvider and injects the redux-form-style props mapped to RHF.
export const withForm =
  ({
    validate,
    warn,
    initialValues: configInitialValues,
    enableReinitialize,
    onChange: onFormChange,
  }: WithFormConfig = {}) =>
  (Component: React.ComponentType<any>) =>
    function RhfForm(props: any) {
      // Prop initialValues override config; enableReinitialize maps to RHF's reactive `values` (re-syncs when initialValues change after mount).
      const initialValues = { ...configInitialValues, ...props.initialValues }
      const methods = useForm({
        defaultValues: initialValues,
        mode: 'onTouched',
        // Forwarded to the resolver as its 2nd arg so validators keep redux-form's `validate(values, props)` signature.
        context: props,
        ...(enableReinitialize ? { values: initialValues } : {}),
        ...(validate ? { resolver: withFormResolver(validate) } : {}),
      })
      const { formState } = methods

      // redux-form form-level `onChange(values, dispatch, props)`: fire on every change; propsRef keeps the callback reading latest props.
      const propsRef = React.useRef(props)
      propsRef.current = props
      React.useEffect(() => {
        if (!onFormChange) return
        const sub = methods.watch(values => onFormChange(values, undefined, propsRef.current))
        return () => sub.unsubscribe()
      }, [methods, onFormChange])

      const content = (
        <Component
          {...props}
          change={(field: string, value: any) =>
            methods.setValue(field, value, { shouldDirty: true, shouldValidate: true })
          }
          dirty={formState.isDirty}
          handleSubmit={methods.handleSubmit}
          invalid={!formState.isValid}
          pristine={!formState.isDirty}
          reset={methods.reset}
          submitting={formState.isSubmitting}
          valid={formState.isValid}
        />
      )

      return (
        <FormProvider {...methods}>
          {warn ? (
            <FormWarnings control={methods.control} warn={warn}>
              {content}
            </FormWarnings>
          ) : (
            content
          )}
          {process.env.NODE_ENV !== 'production' && <DevTool control={methods.control} placement="bottom-left" />}
        </FormProvider>
      )
    }

// Computes redux-form-style warnings reactively for the dual-mode formField (via meta.warning); only mounted when `warn` is present, so useWatch is unconditional.
const FormWarnings = ({ warn, control, children }: any) => {
  const values = useWatch({ control })
  return <FormWarningsContext.Provider value={warn(values) || {}}>{children}</FormWarningsContext.Provider>
}

export default withForm
